import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Add, ArrowOutward, Delete, Edit, Home, MoreHoriz } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../api/axios';
import Layout from '../components/Layout';
import { HouseDialog } from '../components/HouseDialog';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface House {
  id: string;
  name: string;
  shortDescription: string;
  description: string;
  address?: string;
  images?: Array<{
    key: string;
    url: string;
    originalName: string;
  }>;
  host: User;
  coHosts?: User[];
  guests?: User[];
}

const Dashboard: React.FC = () => {
  const [houses, setHouses] = useState<House[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedHouse, setSelectedHouse] = useState<any>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuHouseId, setMenuHouseId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [houseToDelete, setHouseToDelete] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const fetchHouses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/houses');
      setHouses(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('dashboard.failedFetch'));
      console.error('Error fetching houses:', err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response = await axios.get('/users/me');
      setCurrentUser(response.data);
    } catch (err: any) {
      console.error('Error fetching current user:', err);
    }
  }, []);

  useEffect(() => {
    fetchHouses();
    fetchCurrentUser();
  }, [fetchHouses, fetchCurrentUser]);

  const handleHouseClick = (houseId: string) => {
    navigate(`/houses/${houseId}`);
  };

  const handleCreateHouse = () => {
    setSelectedHouse(null);
    setDialogOpen(true);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, houseId: string) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuHouseId(houseId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuHouseId(null);
  };

  const handleEdit = () => {
    const house = houses.find(h => h.id === menuHouseId);
    setSelectedHouse(house);
    setDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setHouseToDelete(menuHouseId);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!houseToDelete) return;

    try {
      await axios.delete(`/houses/${houseToDelete}`);
      setDeleteDialogOpen(false);
      setHouseToDelete(null);
      fetchHouses();
    } catch (err: any) {
      setError(err.response?.data?.message || t('dashboard.failedDelete'));
      console.error('Error deleting house:', err);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setHouseToDelete(null);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedHouse(null);
  };

  const handleHouseCreated = () => {
    fetchHouses(); // Refresh the list
  };

  const isHostedHouse = (house: House) => {
    if (!currentUser) {
      return false;
    }

    return house.host?.id === currentUser.id || Boolean(house.coHosts?.some(member => member.id === currentUser.id));
  };

  const isOwnerHouse = (house: House) => {
    if (!currentUser) {
      return false;
    }

    return house.host?.id === currentUser.id;
  };

  const myHouses = currentUser ? houses.filter(isHostedHouse) : [];
  const guestHouses = currentUser ? houses.filter(house => !isHostedHouse(house)) : [];
  const theme = useTheme();

  const renderHouseCard = (house: House, isHost: boolean) => (
    <Grid item xs={8} sm={6} md={4} key={house.id} sx={{ flexShrink: 0 }}>
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <CardActionArea
          onClick={() => handleHouseClick(house.id)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            flexGrow: 1,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              minHeight: { xs: 250, sm: 300, md: 320 },
              background: house.images?.[0]?.url
                ? `linear-gradient(180deg, rgba(12, 17, 14, 0.04) 0%, rgba(12, 17, 14, 0.68) 100%), url(${house.images[0].url}) center/cover`
                : 'linear-gradient(135deg, rgba(35,68,54,0.92) 0%, rgba(86,120,105,0.86) 48%, rgba(200,107,74,0.72) 100%)',
            }}
          >
            <Box sx={{ position: 'absolute', top: 12, left: 12, right: 12, display: 'flex', justifyContent: 'space-between' }}>
              <Chip
                label={isHost ? t('dashboard.hostingLabel') : t('dashboard.visitingLabel')}
                sx={{
                  bgcolor: 'rgba(255,250,245,0.92)',
                  color: 'text.primary',
                }}
              />
              {isHost && (
                <IconButton
                  size="small"
                  onClick={(event) => handleMenuOpen(event, house.id)}
                  sx={{
                    bgcolor: 'rgba(255,250,245,0.88)',
                    '&:hover': { bgcolor: 'rgba(255,250,245,1)' },
                  }}
                >
                  <MoreHoriz />
                </IconButton>
              )}
            </Box>
            {!house.images?.[0]?.url && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
                <Home sx={{ fontSize: 60, color: 'rgba(255,250,245,0.88)' }} />
              </Box>
            )}
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                p: { xs: 1.5, sm: 2 },
                color: 'common.white',
              }}
            >
              <Typography variant="h6" component="h2" sx={{ color: 'common.white', mb: 0.5 }}>
                {house.name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,250,245,0.84)', mb: 1, display: 'block' }}>
                {house.address || house.host ? `${house.host.firstName} ${house.host.lastName}` : t('dashboard.noDescription')}
              </Typography>
            </Box>
          </Box>
          <CardContent
            sx={{
              p: { xs: 1.5, sm: 2 },
              pb: 0,
              '&:last-child': {
                pb: 0,
              },
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {house.description || t('dashboard.noDescription')}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {t('dashboard.openHouse')}
              </Typography>
              <ArrowOutward fontSize="small" sx={{ color: 'primary.main' }} />
            </Box>
          </CardContent>
        </CardActionArea>
      </Card>
    </Grid>
  );

  return (
    <Layout title="{t('dashboard.layoutTitle')}">
      <Container maxWidth="xl">
        <Paper
          elevation={0}
          sx={{
            mb: 4,
            p: { xs: 2.5, md: 4 },
            borderRadius: 2,
            overflow: 'hidden',
            background:
              'linear-gradient(135deg, rgba(35,68,54,0.98) 0%, rgba(69,98,80,0.94) 45%, rgba(200,107,74,0.88) 100%)',
            color: 'common.white',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: 'radial-gradient(circle at top right, rgba(255,250,245,0.18), transparent 32%)',
            }}
          />
          <Grid container spacing={3} alignItems="flex-end" sx={{ position: 'relative' }}>
            <Grid item xs={12} md={8}>
              <Typography variant="h4" component="h4" sx={{ color: 'common.white', maxWidth: 700 }}>
                {t('dashboard.title')}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1.5, maxWidth: 640, color: 'rgba(255,250,245,0.82)' }}>
                {t('dashboard.subtitle')}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction={{ xs: 'column', sm: 'row', md: 'column' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center', md: 'stretch' }}>
                <Paper
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 0,
                  
                    borderRadius: 2,
                    bgcolor: 'rgba(255,250,245,0.12)',
                    color: 'common.white',
                    backdropFilter: 'blur(12px)',
                    minWidth: 140,
                  }}
                >
                  <Typography variant="overline" sx={{ml: 1, color: 'rgba(255,250,245,0.76)' }}>
                    {t('dashboard.hostingLabel')}
                  </Typography>
                  <Typography variant="h4" sx={{ml: 1, color: 'common.white' }}>
                    {myHouses.length}
                  </Typography>
                </Paper>
                <Paper
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 0,
                    borderRadius: 2,
                    bgcolor: 'rgba(255,250,245,0.12)',
                    color: 'common.white',
                    backdropFilter: 'blur(12px)',
                    minWidth: 140,
                  }}
                >
                  <Typography variant="overline" sx={{ml: 1, color: 'rgba(255,250,245,0.76)' }}>
                    {t('dashboard.visitingLabel')}
                  </Typography>
                  <Typography variant="h4" sx={{ml: 1, color: 'common.white' }}>
                    {guestHouses.length}
                  </Typography>
                </Paper>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<Add />}
                  onClick={handleCreateHouse}
                  size="large"
                  sx={{ alignSelf: { md: 'flex-start' } }}
                >
                  {t('dashboard.createNewHouse')}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : houses.length === 0 ? (
          <Paper
            sx={{
              textAlign: 'center',
              py: 9,
              px: 3,
              borderRadius: 8,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                mx: 'auto',
                mb: 2.5,
                width: 88,
                height: 88,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.08),
              }}
            >
              <Home sx={{ fontSize: 46, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" gutterBottom>
              {t('dashboard.noHouses')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('dashboard.noHousesDescription')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateHouse}
            >
              {t('dashboard.createFirstHouse')}
            </Button>
          </Paper>
        ) : (
          <>
            {myHouses.length > 0 && (
              <Box sx={{ mb: 6 }}>
                <Typography variant="h5" component="h2" sx={{ mb: 3 }}>
                  {t('dashboard.hosted', { count: myHouses.length })}
                </Typography>
                <Grid
                  container
                  columnSpacing={2}
                  rowSpacing={0}
                  sx={{
                    flexWrap: { xs: 'nowrap', sm: 'wrap' },
                    overflowX: { xs: 'auto', sm: 'visible' },
                    scrollSnapType: { xs: 'x mandatory', sm: 'none' },
                    pb: 1,
                    px: { xs: 0.5, sm: 0 },
                    mx: { xs: -0.5, sm: 0 },
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                >
                  {myHouses.map((house) => renderHouseCard(house, true))}
                </Grid>
              </Box>
            )}

            {guestHouses.length > 0 && (
              <Box>
                <Typography variant="h5" component="h2" sx={{ mb: 3 }}>
                  {t('dashboard.guest', { count: guestHouses.length })}
                </Typography>
                <Grid
                  container
                  columnSpacing={2}
                  rowSpacing={0}
                  sx={{
                    flexWrap: { xs: 'nowrap', sm: 'wrap' },
                    overflowX: { xs: 'auto', sm: 'visible' },
                    scrollSnapType: { xs: 'x mandatory', sm: 'none' },
                    pb: 1,
                    px: { xs: 0.5, sm: 0 },
                    mx: { xs: -0.5, sm: 0 },
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                  }}
                >
                  {guestHouses.map((house) => renderHouseCard(house, false))}
                </Grid>
              </Box>
            )}
          </>
        )}
      </Container>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <Edit fontSize="small" sx={{ mr: 1 }} />
          {t('dashboard.menuEdit')}
        </MenuItem>
        {menuHouseId && isOwnerHouse(houses.find(house => house.id === menuHouseId) as House) && (
          <MenuItem onClick={handleDeleteClick}>
            <Delete fontSize="small" sx={{ mr: 1 }} />
            {t('dashboard.menuDelete')}
          </MenuItem>
        )}
      </Menu>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t('dashboard.deleteTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('dashboard.deleteMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('common.actions.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('common.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <HouseDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSuccess={handleHouseCreated}
        house={selectedHouse}
      />
    </Layout>
  );
};

export default Dashboard;
